import MoreVertIcon from "@mui/icons-material/MoreVert";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import VideocamIcon from "@mui/icons-material/Videocam";
import PersonIcon from "@mui/icons-material/Person";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PaymentIcon from "@mui/icons-material/Payment";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import BadgeIcon from "@mui/icons-material/Badge";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import { getUserFullName } from "../../../utils/sessionFields";

interface SessionProps {
  sessionId: string;
  speciality: string;
  modeOfConsultation: string;
  bookedDate: any;
  sessionDate: any;
  sessionMonth: any;
  timeSlots: any;
  description: string;
  doctorImage: string;
  doctorName: string;
  doctorSpeciality: string;
  doctorExperience: string;
  doctorEmail?: string;
  doctorMobile?: string;
  doctorLicense?: string;
  doctorLocation?: string;
  doctorLanguages?: string;
  patientImage: string;
  patientName: string;
  patientNumber: string | number;
  patientEmail: string;
  patientProfileImage: string;
  PatientProfileName: string;
  PatientProfileAge: number;
  PatientProfileGender: string;
  PatientProfileRelation?: string;
  PatientLandmark: string;
  patientAddress1: string;
  patientAddress2: string;
  PatientCity: string;
  consultationFee: number;
  paymentDetails?: {
    amount?: number | null;
    currency?: string;
    status?: string | null;
    paymentMethod?: string | null;
    transactionId?: string | null;
    paymentReference?: string | null;
    paidAt?: string | null;
    invoiceNumber?: string | null;
    invoiceUrl?: string | null;
    originalSubtotal?: number | null;
    discountAmount?: number | null;
    discountedSubtotal?: number | null;
    vatPercentage?: number | null;
    vatAmount?: number | null;
    couponCode?: string | null;
    coupon?: {
      code?: string;
      title?: string;
      type?: string;
      value?: number | null;
      fundedBy?: string | null;
    } | null;
  } | null;
  payTabsDetails?: {
    paymentStatus?: boolean;
    transactionRef?: string | null;
    cartId?: string | null;
    paymentMethod?: string | null;
    cardScheme?: string | null;
    maskedPan?: string | null;
    responseMessage?: string | null;
    amount?: number | null;
    currency?: string;
  } | null;
  handleReportClick: any;
  clinicianClick: any;
  newClinician: any;
  clinicianData?: any;
  symptoms?: any;
  onRefundClick?: () => void;
  onViewRefundStatus?: () => void;
  canProcessRefund?: boolean;
  refundStatus?: string;
  handoverFlags?: {
    isNewClinicianAccepted?: boolean | null;
    isPatientAccepted?: boolean | null;
    hasPendingHandover?: boolean;
  };
}

export const SessionDetailCard = ({
  sessionId,
  speciality,
  modeOfConsultation,
  bookedDate,
  sessionDate,
  sessionMonth,
  timeSlots,
  symptoms,
  description,
  patientImage,
  patientName,
  patientNumber,
  patientEmail,
  patientProfileImage,
  PatientProfileName,
  PatientProfileAge,
  PatientProfileGender,
  PatientProfileRelation = "",
  PatientLandmark,
  patientAddress1,
  patientAddress2,
  PatientCity,
  consultationFee,
  paymentDetails,
  payTabsDetails,
  doctorImage,
  doctorName,
  doctorSpeciality,
  doctorExperience,
  doctorEmail = "N/A",
  doctorMobile = "N/A",
  doctorLicense = "",
  doctorLocation = "",
  doctorLanguages = "",
  handleReportClick,
  clinicianClick,
  newClinician,
  clinicianData,
  onRefundClick,
  onViewRefundStatus,
  canProcessRefund,
  refundStatus,
  handoverFlags,
}: SessionProps) => {
  const theme = useTheme();
  const newClinicianName = getUserFullName(newClinician);

  const handoverPending =
    handoverFlags?.hasPendingHandover &&
    !(
      handoverFlags?.isNewClinicianAccepted === true &&
      handoverFlags?.isPatientAccepted === true
    );

  const canChangeClinician =
    clinicianData?.status === "PAID" &&
    (!handoverFlags?.hasPendingHandover || handoverPending);

  return (
    <Box p={3}>
      {/* Session Overview Card */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: "1px solid rgba(0,0,0,0.08)",
          borderRadius: "12px",
          backgroundColor: "#fafbfc",
        }}
      >
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  backgroundColor: theme.colors.primary.lighter,
                  p: 1,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <MedicalServicesIcon
                  sx={{ color: theme.colors.primary.main }}
                />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: "11px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Session ID
                </Typography>
                <Typography variant="subtitle1" fontWeight={600}>
                  {sessionId}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  backgroundColor: theme.colors.success.lighter,
                  p: 1,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <MedicalServicesIcon
                  sx={{ color: theme.colors.success.main }}
                />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: "11px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Service
                </Typography>
                <Typography variant="subtitle1" fontWeight={600}>
                  {speciality}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  backgroundColor: theme.colors.info.lighter || "#e3f2fd",
                  p: 1,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <VideocamIcon
                  sx={{ color: theme.colors.info.main || "#1976d2" }}
                />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: "11px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Mode
                </Typography>
                <Chip
                  label={modeOfConsultation}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.colors.info.lighter || "#e3f2fd",
                    color: theme.colors.info.main || "#1976d2",
                  }}
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box display="flex" gap={1.5}>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                px={2}
                py={1.5}
                sx={{
                  backgroundColor: theme.colors.warning.lighter,
                  borderRadius: "10px",
                  minWidth: "70px",
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color={theme.colors.warning.dark}
                >
                  {sessionDate}
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color={theme.colors.warning.dark}
                >
                  {sessionMonth}
                </Typography>
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: "11px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Time Slot
                </Typography>
                {timeSlots?.map((item: any, index: number) => (
                  <Box key={index} display="flex" alignItems="center" gap={0.5}>
                    <AccessTimeIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography variant="body2" fontWeight={600}>
                      {item?.timeslot?.startAt} - {item?.timeslot?.endAt}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          <Grid item xs={12}>
            <InfoField label="Symptoms" value={symptoms} />
          </Grid>

          <Grid item xs={12}>
            <InfoField label="Description" value={description} multiline />
          </Grid>
        </Grid>
      </Paper>

      {/* Clinician Details */}
      <Paper
        elevation={0}
        sx={{
          mt: 3,
          border: "1px solid rgba(0,0,0,0.08)",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            backgroundColor: theme.colors.primary.lighter,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                backgroundColor: "#fff",
                p: 1,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LocalHospitalIcon sx={{ color: theme.colors.primary.main }} />
            </Box>
            <Typography variant="h6" fontWeight={600}>
              {newClinician
                ? "Previous Clinician Details"
                : "Clinician Details"}
            </Typography>
          </Box>
          {newClinician ? (
            canChangeClinician && (
              <IconButton
                onClick={clinicianClick}
                sx={{
                  backgroundColor: "#fff",
                  "&:hover": { backgroundColor: "#f5f5f5" },
                }}
              >
                <MoreVertIcon />
              </IconButton>
            )
          ) : (
            canChangeClinician && (
              <IconButton
                onClick={clinicianClick}
                sx={{
                  backgroundColor: "#fff",
                  "&:hover": { backgroundColor: "#f5f5f5" },
                }}
              >
                <MoreVertIcon />
              </IconButton>
            )
          )}
        </Box>
        <Box display="flex" alignItems="center" p={3} gap={2.5}>
          <Avatar
            variant="rounded"
            sx={{
              height: "90px",
              width: "90px",
              fontSize: "40px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
            src={doctorImage}
            alt={doctorName}
          />
          <Box flex={1}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {doctorName || "—"}
            </Typography>
            {doctorSpeciality ? (
              <Chip
                label={doctorSpeciality}
                size="small"
                sx={{
                  backgroundColor: theme.colors.primary.lighter,
                  color: theme.colors.primary.main,
                  fontWeight: 600,
                  mb: 1,
                  mr: 1,
                }}
              />
            ) : null}
            {doctorExperience ? (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  backgroundColor: "#f0f9ff",
                  px: 2,
                  py: 0.5,
                  borderRadius: "8px",
                  mb: 1,
                }}
              >
                <Typography variant="body2" fontWeight={600} color="primary">
                  {doctorExperience} Year
                  {Number(doctorExperience) !== 1 ? "s" : ""} Experience
                </Typography>
              </Box>
            ) : null}
            <Box display="flex" flexDirection="column" gap={0.75} mt={1}>
              {doctorEmail && doctorEmail !== "N/A" ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <EmailIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  <Typography variant="body2" color="text.secondary">
                    {doctorEmail}
                  </Typography>
                </Box>
              ) : null}
              {doctorMobile && doctorMobile !== "N/A" ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <PhoneIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  <Typography variant="body2" color="text.secondary">
                    {doctorMobile}
                  </Typography>
                </Box>
              ) : null}
              {doctorLicense ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <BadgeIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  <Typography variant="body2" color="text.secondary">
                    License: {doctorLicense}
                  </Typography>
                </Box>
              ) : null}
              {doctorLocation ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <LocationOnIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  <Typography variant="body2" color="text.secondary">
                    {doctorLocation}
                  </Typography>
                </Box>
              ) : null}
              {doctorLanguages ? (
                <Typography variant="body2" color="text.secondary">
                  Languages: {doctorLanguages}
                </Typography>
              ) : null}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* New Clinician Details */}
      {newClinician && (
        <Paper
          elevation={0}
          sx={{
            mt: 3,
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2,
              backgroundColor: theme.colors.success.lighter,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  backgroundColor: "#fff",
                  p: 1,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LocalHospitalIcon sx={{ color: theme.colors.success.main }} />
              </Box>
              <Typography variant="h6" fontWeight={600}>
                New Clinician Details
              </Typography>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip
                size="small"
                label={`Clinician: ${
                  handoverFlags?.isNewClinicianAccepted === true
                    ? "Accepted"
                    : handoverFlags?.isNewClinicianAccepted === false
                      ? "Rejected"
                      : "Pending"
                }`}
                color={
                  handoverFlags?.isNewClinicianAccepted === true
                    ? "success"
                    : handoverFlags?.isNewClinicianAccepted === false
                      ? "error"
                      : "warning"
                }
              />
              <Chip
                size="small"
                label={`Patient: ${
                  handoverFlags?.isPatientAccepted === true
                    ? "Accepted"
                    : handoverFlags?.isPatientAccepted === false
                      ? "Rejected"
                      : "Pending"
                }`}
                color={
                  handoverFlags?.isPatientAccepted === true
                    ? "success"
                    : handoverFlags?.isPatientAccepted === false
                      ? "error"
                      : "warning"
                }
              />
            </Box>
          </Box>
          <Box display="flex" alignItems="center" p={3} gap={2.5}>
            <Avatar
              variant="rounded"
              sx={{
                height: "90px",
                width: "90px",
                fontSize: "40px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              }}
              src={newClinician?.imageUrl}
              alt={newClinicianName}
            />
            <Box flex={1}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {newClinicianName || "—"}
              </Typography>
              <Chip
                label={newClinician?.designation}
                size="small"
                sx={{
                  backgroundColor: theme.colors.success.lighter,
                  color: theme.colors.success.main,
                  fontWeight: 600,
                  mb: 1,
                }}
              />
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  backgroundColor: "#f0fdf4",
                  px: 2,
                  py: 0.5,
                  borderRadius: "8px",
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color="success.main"
                >
                  {newClinician?.experience} Year
                  {Number(newClinician?.experience) !== 1 ? "s" : ""} Experience
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Account holder (user who owns patient profiles) */}
      <Paper
        elevation={0}
        sx={{
          mt: 3,
          border: "1px solid rgba(0,0,0,0.08)",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            backgroundColor: "#f3e8ff",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              backgroundColor: "#fff",
              p: 1,
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PersonIcon sx={{ color: "#7c3aed" }} />
          </Box>
          <Typography variant="h6" fontWeight={600}>
            Account Holder
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" p={3} gap={2.5}>
          <Avatar
            variant="rounded"
            sx={{
              height: "90px",
              width: "90px",
              fontSize: "40px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
            src={patientImage}
            alt={patientName}
          />
          <Box flex={1}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {patientName || "—"}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              App user who manages this booking
            </Typography>
            <Box display="flex" flexDirection="column" gap={0.75}>
              <Box display="flex" alignItems="center" gap={1}>
                <EmailIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary">
                  {patientEmail}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <PhoneIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary">
                  {patientNumber}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Patient profile (person receiving care) */}
      <Paper
        elevation={0}
        sx={{
          mt: 3,
          border: "1px solid rgba(0,0,0,0.08)",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            backgroundColor: theme.colors.info.lighter || "#e3f2fd",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              backgroundColor: "#fff",
              p: 1,
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PersonIcon sx={{ color: theme.colors.info.main || "#1976d2" }} />
          </Box>
          <Typography variant="h6" fontWeight={600}>
            Patient Profile
          </Typography>
        </Box>
        <Box
          display="flex"
          p={3}
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Box display="flex" alignItems="center" gap={2.5}>
            <Avatar
              variant="rounded"
              sx={{
                height: "90px",
                width: "90px",
                fontSize: "40px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              }}
              src={patientProfileImage}
              alt={PatientProfileName}
            />
            <Box>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {PatientProfileName || "—"}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Person receiving therapy
              </Typography>
              <Box display="flex" gap={1} mb={0.5} flexWrap="wrap">
                {PatientProfileAge ? (
                  <Chip
                    label={`${PatientProfileAge} years`}
                    size="small"
                    sx={{
                      backgroundColor: theme.colors.info.lighter || "#e3f2fd",
                      color: theme.colors.info.main || "#1976d2",
                      fontWeight: 600,
                    }}
                  />
                ) : null}
                {PatientProfileGender ? (
                  <Chip
                    label={PatientProfileGender}
                    size="small"
                    sx={{
                      backgroundColor: theme.colors.info.lighter || "#e3f2fd",
                      color: theme.colors.info.main || "#1976d2",
                      fontWeight: 600,
                    }}
                  />
                ) : null}
                {PatientProfileRelation ? (
                  <Chip
                    label={PatientProfileRelation}
                    size="small"
                    sx={{
                      backgroundColor: theme.colors.info.lighter || "#e3f2fd",
                      color: theme.colors.info.main || "#1976d2",
                      fontWeight: 600,
                    }}
                  />
                ) : null}
              </Box>
            </Box>
          </Box>
          <Button
            variant="contained"
            onClick={handleReportClick}
            sx={{
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              "&:hover": {
                boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
              },
            }}
          >
            Medical Records
          </Button>
        </Box>
      </Paper>

      {/* Patient Location Details */}
      {clinicianData?.consultationMode === "HOME" && (
        <Paper
          elevation={0}
          sx={{
            mt: 3,
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2,
              backgroundColor: theme.colors.warning.lighter,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                backgroundColor: "#fff",
                p: 1,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LocationOnIcon sx={{ color: theme.colors.warning.main }} />
            </Box>
            <Typography variant="h6" fontWeight={600}>
              Patient Location Details
            </Typography>
          </Box>
          <Box p={3}>
            <Box display="flex" gap={2} mb={2}>
              <Box
                sx={{
                  backgroundColor: theme.colors.warning.lighter,
                  p: 1,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "center",
                }}
              >
                <LocationOnIcon sx={{ color: theme.colors.warning.main }} />
              </Box>
              <Box flex={1}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {PatientCity}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {patientAddress1}
                  {patientAddress2 && `, ${patientAddress2}`}
                  {PatientLandmark && `, ${PatientLandmark}`}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={2} alignItems="center">
              <Box
                sx={{
                  backgroundColor: theme.colors.info.lighter || "#e3f2fd",
                  p: 1,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PhoneIcon
                  sx={{
                    color: theme.colors.info.main || "#1976d2",
                    fontSize: 20,
                  }}
                />
              </Box>
              <Typography variant="body2" fontWeight={600}>
                {patientNumber}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Consultation Bill Details */}
      <Paper
        elevation={0}
        sx={{
          mt: 3,
          border: "1px solid rgba(0,0,0,0.08)",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            backgroundColor: theme.colors.success.lighter,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              backgroundColor: "#fff",
              p: 1,
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PaymentIcon sx={{ color: theme.colors.success.main }} />
          </Box>
          <Typography variant="h6" fontWeight={600}>
            Consultation Bill Details
          </Typography>
        </Box>

        <Box p={3}>
          {(() => {
            const currency = paymentDetails?.currency || "SAR";
            const money = (value?: number | null) =>
              value == null || Number.isNaN(Number(value))
                ? "—"
                : `${Number(value).toFixed(2)} ${currency}`;
            const rows: Array<{ label: string; value: string; emphasize?: boolean; muted?: boolean }> = [
              {
                label: "Original Subtotal",
                value: money(paymentDetails?.originalSubtotal),
              },
              {
                label: paymentDetails?.couponCode
                  ? `Coupon (${paymentDetails.couponCode})`
                  : "Discount",
                value:
                  paymentDetails?.discountAmount && paymentDetails.discountAmount > 0
                    ? `−${money(paymentDetails.discountAmount)}`
                    : "—",
                muted: !(paymentDetails?.discountAmount && paymentDetails.discountAmount > 0),
              },
              {
                label: "After Discount",
                value: money(paymentDetails?.discountedSubtotal),
              },
              {
                label:
                  paymentDetails?.vatPercentage != null
                    ? `VAT (${paymentDetails.vatPercentage}%)`
                    : "VAT",
                value: money(paymentDetails?.vatAmount),
              },
              {
                label: "Amount Paid",
                value: money(paymentDetails?.amount ?? consultationFee),
                emphasize: true,
              },
            ];

            return (
              <Box display="flex" flexDirection="column" gap={1.5}>
                {paymentDetails?.couponCode && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "8px",
                      backgroundColor: theme.colors.warning?.lighter || "#fff8e1",
                      border: "1px solid rgba(245, 124, 0, 0.25)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" display="block">
                      Coupon Applied
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {paymentDetails.couponCode}
                      {paymentDetails.coupon?.title ? ` — ${paymentDetails.coupon.title}` : ""}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                      {[
                        paymentDetails.coupon?.type,
                        paymentDetails.coupon?.value != null
                          ? `Value: ${paymentDetails.coupon.value}`
                          : null,
                        paymentDetails.coupon?.fundedBy
                          ? `Funded by: ${paymentDetails.coupon.fundedBy}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "Discount applied to this booking"}
                    </Typography>
                  </Box>
                )}

                {rows.map((row) => (
                  <Box
                    key={row.label}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      backgroundColor: row.emphasize ? "#fafbfc" : "transparent",
                      p: row.emphasize ? 2 : 1,
                      borderRadius: "8px",
                      border: row.emphasize ? "1px solid rgba(0,0,0,0.06)" : "none",
                    }}
                  >
                    <Typography
                      variant="body1"
                      fontWeight={row.emphasize ? 600 : 500}
                      color={row.muted ? "text.secondary" : "text.primary"}
                    >
                      {row.label}
                    </Typography>
                    <Typography
                      variant={row.emphasize ? "h6" : "body1"}
                      fontWeight={row.emphasize ? 700 : 600}
                      color={
                        row.emphasize
                          ? theme.colors.success.main
                          : row.muted
                            ? "text.secondary"
                            : "text.primary"
                      }
                    >
                      {row.value}
                    </Typography>
                  </Box>
                ))}

                <Divider sx={{ my: 1 }} />

                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Payment Status
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {paymentDetails?.status ||
                        (payTabsDetails?.paymentStatus ? "PAID" : "—")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Payment Method
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {paymentDetails?.paymentMethod ||
                        payTabsDetails?.paymentMethod ||
                        "—"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Transaction Ref
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-all" }}>
                      {payTabsDetails?.transactionRef ||
                        paymentDetails?.transactionId ||
                        paymentDetails?.paymentReference ||
                        "—"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Card
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {[payTabsDetails?.cardScheme, payTabsDetails?.maskedPan]
                        .filter(Boolean)
                        .join(" · ") || "—"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Paid At
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {paymentDetails?.paidAt
                        ? new Date(paymentDetails.paidAt).toLocaleString()
                        : "—"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Invoice
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {paymentDetails?.invoiceUrl ? (
                        <a
                          href={paymentDetails.invoiceUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {paymentDetails.invoiceNumber || "View invoice"}
                        </a>
                      ) : (
                        paymentDetails?.invoiceNumber || "—"
                      )}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            );
          })()}

          {refundStatus && refundStatus !== "NONE" && (
            <Box mt={2}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  backgroundColor:
                    refundStatus === "COMPLETED"
                      ? "rgba(46, 125, 50, 0.1)"
                      : refundStatus === "PROCESSING"
                        ? "rgba(25, 118, 210, 0.1)"
                        : refundStatus === "FAILED"
                          ? "rgba(211, 47, 47, 0.1)"
                          : "rgba(237, 108, 2, 0.1)",
                  border: `1px solid ${refundStatus === "COMPLETED"
                      ? "rgba(46, 125, 50, 0.3)"
                      : refundStatus === "PROCESSING"
                        ? "rgba(25, 118, 210, 0.3)"
                        : refundStatus === "FAILED"
                          ? "rgba(211, 47, 47, 0.3)"
                          : "rgba(237, 108, 2, 0.3)"
                    }`,
                  borderRadius: "8px",
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" fontWeight={500}>
                    Refund Status:
                  </Typography>
                  <Chip
                    label={refundStatus}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      backgroundColor:
                        refundStatus === "COMPLETED"
                          ? theme.colors.success.main
                          : refundStatus === "PROCESSING"
                            ? theme.colors.info.main
                            : refundStatus === "FAILED"
                              ? theme.colors.error.main
                              : theme.colors.warning.main,
                      color: "#fff",
                    }}
                  />
                </Box>
              </Paper>
            </Box>
          )}

          <Box mt={2} display="flex" gap={2} flexWrap="wrap">
            {canProcessRefund && onRefundClick && (
              <Button
                variant="contained"
                color="error"
                fullWidth={!onViewRefundStatus}
                onClick={onRefundClick}
                sx={{
                  flex: onViewRefundStatus ? 1 : undefined,
                  boxShadow: "0 4px 12px rgba(211, 47, 47, 0.25)",
                  "&:hover": {
                    boxShadow: "0 6px 16px rgba(211, 47, 47, 0.35)",
                  },
                }}
              >
                Process Refund
              </Button>
            )}
            {onViewRefundStatus && (
              <Button
                variant="outlined"
                fullWidth={!canProcessRefund}
                onClick={onViewRefundStatus}
                sx={{
                  flex: canProcessRefund ? 1 : undefined,
                  borderWidth: 2,
                  "&:hover": {
                    borderWidth: 2,
                  },
                }}
              >
                View Refund History
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

const InfoField = ({
  label,
  value,
  multiline = false,
}: {
  label: string;
  value: string;
  multiline?: boolean;
}) => {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontSize: "11px",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          fontWeight: 600,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          mt: 0.5,
          whiteSpace: multiline ? "pre-wrap" : "normal",
        }}
      >
        {value || "N/A"}
      </Typography>
    </Box>
  );
};
